import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import LabelText from '@/components/Form/LabelText';
import InputDate from '@/components/Input/InputDate';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import React, { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

const StepContainer = styled.div`
  header {
    line-height: 1.5;
    border-bottom: 1px solid #1cabe2;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }
  article {
    border-bottom: 1px solid #b7b8ba;
    margin-bottom: 24px;

    &:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  ${FormGroup} {
    .tip {
      font-size: 14px;
      margin-top: 8px;
      font-weight: 100;
      ${breakpoint(640)} {
        font-size: 12px;
      }
    }

    &.flex {
      margin-bottom: 24px;
      .col1 {
        width: 192px;
      }
      .col2 {
        width: calc(100% - 192px);
      }
    }
  }
  .btn-wrap {
    margin-top: 80px;
  }
  ${breakpoint(`mobile`)} {
    ${FormGroup} {
      &.flex {
        margin-bottom: 24px;
        .col1,
        .col2 {
          width: 100%;
        }
        .col1 {
          margin-bottom: 24px;
        }
      }
    }
  }
`;
export interface PartyApplyStep2Data {
  partyName: string;
  partyType: string;
  partyDate: string;
  partyPlace: string;
  partyTimetable: string;
  partyAmount: string;
  answerReason: string;
  answerPlan: string;
  answerField: string;
  answerExpected: string;
  partySns?: string;
}
interface PartyApplyCompleteProps {
  onSubmit: (data: PartyApplyStep2Data) => void;
  data?: any;
}
const ParrtAppyStep2: FC<PartyApplyCompleteProps> = ({ onSubmit }) => {
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PartyApplyStep2Data>({
    defaultValues: {
      partyName: ``,
      partyType: ``,
      partyDate: ``,
      partyPlace: ``,
      partyTimetable: ``,
      partyAmount: ``,
      answerReason: ``,
      answerPlan: ``,
      answerField: ``,
      answerExpected: ``,
      partySns: ``,
    },
  });
  const onSubmitForm: SubmitHandler<PartyApplyStep2Data> = useCallback(
    (formData) => {
      onSubmit(formData);
      console.log(formData);
    },
    [onSubmit],
  );

  return (
    <StepContainer>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="form-container">
          <header>
            <h3>
              <Tit size="s2">2. 파티 계획서 작성</Tit>
            </h3>
          </header>
          <article>
            <FormGroup className="FormGroup">
              <LabelText require>모금 파티·활동명</LabelText>
              <InputText
                placeholder="모금 파티·활동명 입력"
                name="partyName"
                ref={register({
                  required: {
                    value: true,
                    message: `모금 파티·활동명을 입력해주세요.`,
                  },
                  maxLength: {
                    value: 50,
                    message: `50자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.partyName && (
                <ErrorMessage>{errors.partyName.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>모금 파티·활동 종류</LabelText>
              <InputText
                placeholder="모금 파티·활동 종류 입력"
                name="partyType"
                ref={register({
                  required: {
                    value: true,
                    message: `모금 파티·활동 종류를 입력해주세요.`,
                  },
                  maxLength: {
                    value: 20,
                    message: `20자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.partyType && (
                <ErrorMessage>{errors.partyType.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup flex">
              <div className="col1">
                <LabelText require>날짜</LabelText>
                <InputDate
                  placeholder="2021.01.01"
                  name="partyDate"
                  ref={register({
                    required: {
                      value: true,
                      message: `날짜를 선택해주세요.`,
                    },
                  })}
                />
              </div>
              <div className="col2">
                <LabelText require>장소</LabelText>
                <InputText
                  placeholder="장소 입력"
                  name="partyPlace"
                  ref={register({
                    required: {
                      value: true,
                      message: `장소를 입력해주세요.`,
                    },
                    maxLength: {
                      value: 50,
                      message: `50자 이내로 입력해주세요.`,
                    },
                  })}
                />
              </div>
              {errors.partyDate && (
                <ErrorMessage>{errors.partyDate.message}</ErrorMessage>
              )}
              {errors.partyPlace && (
                <ErrorMessage>{errors.partyPlace.message}</ErrorMessage>
              )}
              <p className="tip">
                공공장소를 사용하는 경우, 파티 주최자가 직접 장소 사용에 관한
                사전 승인을 받아야 합니다.
              </p>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>세부 예상 일정</LabelText>
              <InputTextarea
                placeholder="세부 예상 일정을 시간별로 간략히 입력해 주세요."
                name="partyTimetable"
                ref={register({
                  required: {
                    value: true,
                    message: `세부 예상 일정을 입력해주세요.`,
                  },
                  maxLength: {
                    value: 500,
                    message: `500자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.partyTimetable && (
                <ErrorMessage>{errors.partyTimetable.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>기대되는 예상 모금액(원)</LabelText>
              <InputText
                placeholder="예상 모금액 입력"
                name="partyAmount"
                ref={register({
                  required: {
                    value: true,
                    message: `예상 모금액을 입력해주세요.`,
                  },
                  pattern: {
                    value: /^[0-9]/g,
                    message: `숫자만 입력해주세요.`,
                  },
                  maxLength: {
                    value: 12,
                    message: `12자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.partyAmount && (
                <ErrorMessage>{errors.partyAmount.message}</ErrorMessage>
              )}
            </FormGroup>
          </article>
          <article>
            <FormGroup className="FormGroup">
              <LabelText require>
                1 . 유니세프한국위원회를 통해 모금활동을 하고 싶은 이유는
                무엇인가요?
              </LabelText>
              <InputTextarea
                placeholder="유니세프한국위원회를 통해 모금활동을 하고 싶은 이유를 알려주세요."
                name="answerReason"
                ref={register({
                  required: {
                    value: true,
                    message: `유니세프한국위원회를 통해 모금활동을 하고 싶은 이유를 작성해주세요.`,
                  },
                  maxLength: {
                    value: 500,
                    message: `500자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.answerReason && (
                <ErrorMessage>{errors.answerReason.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                2 . Party for UNICEF에 참가하는 사람들에게 유니세프를 어떻게
                소개 할 계획인가요? (유니세프 후원방법, 유니세프 사업소개 등
                유니세프를 어떻게 이해하고 있는지 간략히 서술)
              </LabelText>
              <InputTextarea
                placeholder="유니세프를 어떻게 소개할 계획인지 알려주세요."
                name="answerPlan"
                ref={register({
                  required: {
                    value: true,
                    message: `유니세프를 소개할 계획을 작성해주세요.`,
                  },
                  maxLength: {
                    value: 500,
                    message: `500자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.answerPlan && (
                <ErrorMessage>{errors.answerPlan.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                3 . 유니세프의 어떤 사업을 후원하고 싶으신가요? (예: 영양,
                식수와 위생, 보건, 에이즈, 교육, 어린이 보호 등)
              </LabelText>
              <InputTextarea
                placeholder="유니세프에서 후원하고 싶은 사업을 입력해 주세요."
                name="answerField"
                ref={register({
                  required: {
                    value: true,
                    message: `유니세프에서 후원하고 싶은 사업을 작성해주세요.`,
                  },
                  maxLength: {
                    value: 500,
                    message: `500자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.answerField && (
                <ErrorMessage>{errors.answerField.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                4 . 모금 파티를 어떻게 알릴 예정이신가요?
              </LabelText>
              <InputTextarea
                placeholder="모금 파티를 알릴 방법을 입력해 주세요."
                name="answerExpected"
                ref={register({
                  required: {
                    value: true,
                    message: `모금 파티를 알릴 방법을 작성해주세요.`,
                  },
                  maxLength: {
                    value: 500,
                    message: `500자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.answerExpected && (
                <ErrorMessage>{errors.answerExpected.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>5. 관련 SNS</LabelText>
              <InputText
                placeholder="모금 파티 관련 SNS를 개설했다면, 주소를 알려주세요. (선택)"
                name="partySns"
                ref={register({
                  maxLength: {
                    value: 50,
                    message: `50자 이내로 입력해주세요.`,
                  },
                })}
              />
              {errors.partySns && (
                <ErrorMessage>{errors.partySns.message}</ErrorMessage>
              )}
            </FormGroup>

            <div className="btn-wrap">
              <Button type="submit" full>
                주의사항 동의
              </Button>
            </div>
          </article>
        </div>
      </form>
    </StepContainer>
  );
};
export default ParrtAppyStep2;
